import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { tourService, TourProgress } from '../services/tourService';
import { tourDefinitionService, TourDefinitionResponse, TourStepDto } from '../services/tourDefinitionService';
import { logService } from '../services/logService';
import SimpleTour from '../components/Tour/SimpleTour';
import { fallbackTours } from '../tours/definitions';

export interface TourStep extends TourStepDto {}

export interface TourDefinition extends TourDefinitionResponse {}

interface ActiveTour {
  id: number;
  name: string;
  title: string;
  steps: TourStep[];
}

interface TourContextType {
  // Methods
  startTour: (tourName: string, autoStart?: boolean) => void;
  startTourFromBeginning: (tourName: string) => void;
  skipTour: (tourName: string) => void;
  completeTour: (tourName: string) => void;
  getTourProgress: (tourName: string) => Promise<TourProgress>;
  
  // Registration (for backward compatibility with hardcoded tours)
  registerTour: (tour: { name: string; title: string; steps: TourStep[]; formId?: string; run?: boolean; continuous?: boolean }) => void;
  unregisterTour: (tourName: string) => void;
  
  // New dynamic methods
  loadToursFromAPI: () => Promise<void>;
  getTourForCurrentRoute: () => TourDefinition | null;
  
  // State
  isTourActive: boolean;
  activeTour: string | null;
  resetDismissedTours: () => void;
  clearTourCache: (tourName?: string) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isTourActive, setIsTourActive] = useState(false);
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourDefinitions, setTourDefinitions] = useState<Map<string, TourDefinition>>(new Map());
  
  // Legacy map for backward compatibility with hardcoded tours
  const legacyTourMap = useRef<Map<string, any>>(new Map());
  const [tourProgress, setTourProgress] = useState<Map<string, TourProgress>>(new Map());
  const [userClosedTour, setUserClosedTour] = useState(false);
  const autoStartInProgress = useRef(false);
  const initializedTours = useRef<Set<string>>(new Set());
  
  // New: Route to tour mapping for dynamic system
  const [routeToTourMap, setRouteToTourMap] = useState<Map<string, string>>(new Map());
  const [apiToursLoaded, setApiToursLoaded] = useState<boolean | undefined>(undefined); // undefined = checking, true = success, false = failed

  // Initialize tour progress once on mount
  useEffect(() => {
    const initializeTours = async () => {
      try {
        const progress = await tourService.getAllTourProgress();
        const progressMap = new Map<string, TourProgress>();
        progress.forEach(p => progressMap.set(p.tourName, p));
        setTourProgress(progressMap);
      } catch (error: any) {
        // If API fails, initialize with empty map
        setTourProgress(new Map());
      }
    };

    initializeTours();
  }, []);

  // Get tour progress with caching
  const getTourProgress = useCallback(async (tourName: string): Promise<TourProgress> => {
    // Check cache first
    if (tourProgress.has(tourName)) {
      return tourProgress.get(tourName)!;
    }

    try {
      const progress = await tourService.getTourProgress(tourName);
      setTourProgress(prev => new Map(prev).set(tourName, progress));
      return progress;
    } catch (error: any) {
      // Return default progress for all errors
      const defaultProgress = {
        tourName,
        stepNumber: 0,
        isCompleted: false,
        lastCompletedAt: null
      };
      setTourProgress(prev => new Map(prev).set(tourName, defaultProgress));
      return defaultProgress;
    }
  }, [tourProgress]);

  // Start tour with proper state management
  const startTour = useCallback(async (tourName: string, autoStart: boolean = false) => {
    // First try to get from tourDefinitions, then fallback to legacyTourMap
    let tour = tourDefinitions.get(tourName);
    if (!tour) {
      tour = legacyTourMap.current.get(tourName);
    }
    
    if (!tour) {
      console.warn(`Tour "${tourName}" not found`);
      return;
    }

    // Prevent multiple simultaneous starts
    if (isTourActive && activeTour === tourName) {
      return;
    }

    try {
      const progress = await getTourProgress(tourName);
      
      if (progress.isCompleted && !autoStart) {
        if (!window.confirm('این تور قبلاً تکمیل شده است. آیا می‌خواهید دوباره اجرا شود؟')) {
          return;
        }
      } else if (progress.isCompleted && autoStart) {
        return; // Don't auto-start if already completed
      }

      setUserClosedTour(false);
      setActiveTour(tourName);
      setCurrentStep(progress.stepNumber);
      setIsTourActive(true);
    } catch (error) {
      console.error('Error starting tour:', error);
    }
  }, [isTourActive, activeTour, getTourProgress]);

  // Start tour from beginning (force start from step 0)
  const startTourFromBeginning = useCallback(async (tourName: string) => {
    // First try to get from tourDefinitions, then fallback to legacyTourMap
    let tour = tourDefinitions.get(tourName);
    if (!tour) {
      tour = legacyTourMap.current.get(tourName);
    }
    
    if (!tour) {
      console.warn(`Tour "${tourName}" not found`);
      return;
    }

    // Force start from step 0, ignoring any progress
    setUserClosedTour(false);
    setActiveTour(tourName);
    setCurrentStep(0); // Always start from step 0
    setIsTourActive(true);
    
    // Update progress to step 0 in backend (but don't wait for it)
    try {
      await tourService.updateTourProgress({
        tourName,
        stepNumber: 0,
        isCompleted: false,
      });
    } catch (error) {
      // Silently handle errors - don't block the tour start
    }
  }, []);

  // Skip tour
  const skipTour = useCallback(async (tourName: string) => {
    try {
      await tourService.updateTourProgress({
        tourName,
        stepNumber: currentStep,
        isCompleted: true,
      });
    } catch (error) {
      // Silently handle errors
    }
    
    // Close the tour immediately
    setUserClosedTour(true);
    setIsTourActive(false);
    setActiveTour(null);
    setCurrentStep(0);
    
    // Save to localStorage that this tour was dismissed in this session
    const dismissed = JSON.parse(localStorage.getItem('dismissedTours') || '[]');
    if (!dismissed.includes(tourName)) {
      dismissed.push(tourName);
      localStorage.setItem('dismissedTours', JSON.stringify(dismissed));
    }
  }, [currentStep]);

  // Complete tour
  const completeTour = useCallback(async (tourName: string) => {
    try {
      await tourService.updateTourProgress({
        tourName,
        stepNumber: currentStep,
        isCompleted: true,
      });
      
      // Update progress cache
      setTourProgress(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(tourName);
        if (existing) {
          newMap.set(tourName, { ...existing, isCompleted: true });
        }
        return newMap;
      });
    } catch (error) {
      // Silently handle errors
    }
    
    // Close the tour immediately
    setUserClosedTour(true);
    setIsTourActive(false);
    setActiveTour(null);
    setCurrentStep(0);
  }, [currentStep]);

  // Register tour with proper state management
  const registerTour = useCallback((tour: { name: string; title: string; steps: TourStep[]; formId?: string; run?: boolean; continuous?: boolean }) => {
    // Prevent duplicate registration
    if (initializedTours.current.has(tour.name)) {
      return;
    }

    // Check global tour setting
    const globalEnabled = localStorage.getItem('tourEnabled') === 'true';
    
    // Check individual tour setting
    const savedStates = localStorage.getItem('tourStates');
    const tourStates = savedStates ? JSON.parse(savedStates) : {};
    const tourEnabled = tourStates[tour.name] ?? tour.run ?? false;
    
    // Check if tour was dismissed in this browser session
    const dismissed = JSON.parse(localStorage.getItem('dismissedTours') || '[]');
    const wasDismissed = dismissed.includes(tour.name);
    
    // Create TourDefinition object from legacy tour
    const tourDefinition: TourDefinition = {
      id: Date.now(), // Use timestamp as temporary ID
      name: tour.name,
      title: tour.title,
      formId: tour.formId || '',
      steps: tour.steps,
      isActive: true,
      runAutomatically: tour.run ?? false,
      isContinuous: tour.continuous ?? true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Store tour in tourDefinitions (main storage)
    setTourDefinitions(prev => new Map(prev).set(tour.name, tourDefinition));
    
    // Also store in legacy map for backward compatibility
    legacyTourMap.current.set(tour.name, tour);
    
    // Mark as initialized
    initializedTours.current.add(tour.name);
    
    // Auto-start tour if conditions are met
    if (globalEnabled && tourEnabled && !wasDismissed && !autoStartInProgress.current) {
      autoStartInProgress.current = true;
      
      // Use setTimeout to prevent state updates during render
      // Add more delay to ensure tour is registered before starting
      setTimeout(() => {
        startTour(tour.name, true);
        autoStartInProgress.current = false;
      }, 500);
    }
  }, []);

  // Unregister tour
  const unregisterTour = useCallback((tourName: string) => {
    setTourDefinitions(prev => {
      const newMap = new Map(prev);
      newMap.delete(tourName);
      return newMap;
    });
    initializedTours.current.delete(tourName);
  }, []);

  // Handle next step
  const handleNext = useCallback(async () => {
    if (activeTour) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      try {
        await tourService.updateTourProgress({
          tourName: activeTour,
          stepNumber: nextStep,
          isCompleted: false,
        });
      } catch (error) {
        // Silently handle errors
      }
    }
  }, [activeTour, currentStep]);

  // Handle previous step
  const handlePrevious = useCallback(async () => {
    if (activeTour && currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      try {
        await tourService.updateTourProgress({
          tourName: activeTour,
          stepNumber: prevStep,
          isCompleted: false,
        });
      } catch (error) {
        // Silently handle errors
      }
    }
  }, [activeTour, currentStep]);

  // Clear tour cache
  const clearTourCache = useCallback((tourName?: string) => {
    if (tourName) {
      // Clear specific tour from cache
      setTourProgress(prev => {
        const newMap = new Map(prev);
        newMap.delete(tourName);
        return newMap;
      });
    } else {
      // Clear all tour cache
      setTourProgress(new Map());
    }
  }, []);

  // Helper function to determine route key for a tour
  const getRouteKeyForTour = useCallback((tour: TourDefinition): string | null => {
    // Strategy 1: Use formId to determine route
    if (tour.formId) {
      const formIdToRouteMap: Record<string, string> = {
        'Dashboard': '/dashboard',
        'ProjectList': '/projects',
        'PayrollConfiguration': '/payroll/config',
        'EmployeeList': '/payroll/employees',
        'ContractList': '/contracts',
        // Add more mappings as needed
      };
      
      const route = formIdToRouteMap[tour.formId];
      if (route) return route;
    }
    
    // Strategy 2: Use tour name to determine route
    const nameToRouteMap: Record<string, string> = {
      'dashboard': '/dashboard',
      'projects': '/projects',
      'payroll-config': '/payroll/config',
      'employees': '/payroll/employees',
      'contracts': '/contracts',
      // Add more mappings as needed
    };
    
    return nameToRouteMap[tour.name] || null;
  }, []);

  // Load tours from API and create route mapping
  const loadToursFromAPI = useCallback(async () => {
    // Temporarily disable API loading to prevent errors
    // When API is implemented, this can be re-enabled
    setApiToursLoaded(false); // Mark as failed to trigger fallback
    
    // Future implementation when API is ready:
    /*
    if (apiToursLoaded) return; // Avoid duplicate loading
    
    try {
      const tours = await tourDefinitionService.getAll();
      const newRouteToTourMap = new Map<string, string>();
      
      tours.forEach(tour => {
        // Store tour in definitions
        setTourDefinitions(prev => new Map(prev).set(tour.name, tour));
        
        // Create route mapping based on formId or tour name
        const routeKey = getRouteKeyForTour(tour);
        if (routeKey) {
          newRouteToTourMap.set(routeKey, tour.name);
        }
      });
      
      setRouteToTourMap(newRouteToTourMap);
      setApiToursLoaded(true);
    } catch (error) {
      setApiToursLoaded(false); // Mark as not loaded to trigger fallback
    }
    */
  }, []);

  // Load fallback tours if API failed or is unavailable
  useEffect(() => {
    // Only load fallbacks if we've determined API is not available
    if (apiToursLoaded === true) return; // API was successful, don't load fallbacks
    if (apiToursLoaded === undefined) return; // Still checking API, wait
    
    // API failed or is not available, load fallback tours
    const { projectsTour, dashboardTour, payrollConfigTour } = require('../tours/definitions');
    const fallbackTourList = [projectsTour, dashboardTour, payrollConfigTour];
    
    // Create route mapping for fallback tours
    const fallbackRouteMap = new Map<string, string>();
    fallbackTourList.forEach(tour => {
      const routeKey = getRouteKeyForTour(tour);
      if (routeKey) {
        fallbackRouteMap.set(routeKey, tour.name);
      }
    });
    setRouteToTourMap(fallbackRouteMap);
    
    // Register fallback tours
    fallbackTourList.forEach(tour => {
      if (!initializedTours.current.has(tour.name)) {
        registerTour(tour);
      }
    });
  }, [apiToursLoaded, registerTour, getRouteKeyForTour]);

  // Get tour for current route (dynamic detection)
  const getTourForCurrentRoute = useCallback((): TourDefinition | null => {
    // Try to get current route from window location
    const currentPath = window.location.pathname;
    
    // Find matching tour in route mapping
    for (const [route, tourName] of routeToTourMap.entries()) {
      if (currentPath === route || currentPath.startsWith(route + '/')) {
        return tourDefinitions.get(tourName) || null;
      }
    }
    
    return null;
  }, [routeToTourMap, tourDefinitions]);

  // Load tours from API on mount (dynamic system)
  useEffect(() => {
    loadToursFromAPI();
  }, [loadToursFromAPI]);

  // Reset dismissed tours
  const resetDismissedTours = useCallback(() => {
    // Clear all tour-related localStorage items
    localStorage.removeItem('dismissedTours');
    localStorage.removeItem('tourStates');
    localStorage.removeItem('tourEnabled');
    
    // Clear tour progress cache
    setTourProgress(new Map());
    
    // Clear initialized tours to allow re-registration
    initializedTours.current.clear();
    
    // Close any active tour
    setIsTourActive(false);
    setActiveTour(null);
    setCurrentStep(0);
    setUserClosedTour(false);
    
    // Reload page to apply changes and restart tours from step 1
    window.location.reload();
  }, []);

  const activeTourDefinition = activeTour ? tourDefinitions.get(activeTour) : null;

  return (
    <TourContext.Provider
      value={{
        startTour,
        startTourFromBeginning,
        skipTour,
        completeTour,
        getTourProgress,
        isTourActive,
        activeTour,
        registerTour,
        unregisterTour,
        resetDismissedTours,
        clearTourCache,
        loadToursFromAPI,
        getTourForCurrentRoute,
      }}
    >
      {children}
      {isTourActive && activeTourDefinition && activeTour && (
        <SimpleTour
          steps={activeTourDefinition.steps}
          currentStep={currentStep}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onClose={() => skipTour(activeTour)}
          onComplete={() => completeTour(activeTour)}
          isOpen={isTourActive}
        />
      )}
    </TourContext.Provider>
  );
};
